import React, { useState, useEffect } from 'react';
import { Tooltip } from 'react-tooltip';
import orderApi from '../../../services/orderApi';
import NoTransactions from '../../../assets/no-transactions.svg';
import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import OrdersSummarySkeleton from './components/OrdersSummarySkeleton';
import {
  PageHeader,
  TWDataTable,
  OrderStatusBadge,
  EmptyState,
} from '../../../components/ui';
import { headerCls } from '../../../data/columns';
import { formatCurrency } from '../../../utils/baseUtils';
import { netPayout } from '../../../utils/orderTotals';

dayjs.extend(customParseFormat);
const utc = require('dayjs/plugin/utc');
const timezone = require('dayjs/plugin/timezone');

dayjs.extend(utc);
dayjs.extend(timezone);

const numCell = 'text-sm tabular-nums text-slate-600 dark:text-slate-300';
const negCell = `${numCell} text-rose-600 dark:text-rose-400`;

const OrdersSummary = () => {
  const [orderList, setOrderList] = useState([]);
  const [pagination, setPagination] = useState({
    pageNumber: 1,
    pageSize: 20,
    totalRecords: 0,
  });
  const [loading, setLoading] = useState({ listLoading: false });

  const getOrderList = async () => {
    setLoading((prev) => ({ ...prev, listLoading: true }));
    try {
      const query = {
        pageNumber: pagination.pageNumber,
        pageSize: pagination.pageSize,
        deleted: false,
        paymentstatus: 20,
      };
      const list = await orderApi.list(query);
      setOrderList(list.data.data);
      setPagination({
        pageNumber: list.data.pageNumber,
        pageSize: list.data.pageSize,
        totalRecords: list.data.totalRecords,
      });
    } catch (error) {
      // silent
    } finally {
      setLoading((prev) => ({ ...prev, listLoading: false }));
    }
  };

  const calculateNetPayout = (order) => {
    const raw = netPayout(order);
    if (order.total < 0) return `(${formatCurrency(Math.abs(raw))})`;
    return formatCurrency(raw);
  };

  const orderWithExtraFees =
    orderList?.find((order) => order.extrafees?.length > 0)?.extrafees || [];

  useEffect(() => {
    getOrderList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageNumber, pagination.pageSize]);

  const showStoreFees = orderWithExtraFees.length > 0;

  const columns = [
    {
      title: <span className={headerCls}>Order#</span>,
      key: 'orderid',
      render: (_, order) => (
        <span className="text-sm font-bold text-slate-900 dark:text-slate-100">
          {order.orderid}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Date</span>,
      key: 'date',
      render: (_, order) => (
        <span className="text-sm font-medium text-slate-700 dark:text-slate-200">
          {dayjs(order.createdate).format('MM/DD/YYYY hh:mm A')}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Company</span>,
      key: 'company',
      render: (_, order) => (
        <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
          {order.store?.company?.name || '—'}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Status</span>,
      key: 'status',
      render: (_, order) => <OrderStatusBadge status={order.orderstatus} />,
    },
    {
      title: <span className={headerCls}>Subtotal</span>,
      key: 'subtotal',
      render: (_, order) => (
        <span className={numCell}>{formatCurrency(order.subtotal)}</span>
      ),
    },
    {
      title: <span className={headerCls}>Tax</span>,
      key: 'tax',
      render: (_, order) => (
        <span className={numCell}>{formatCurrency(order.tax)}</span>
      ),
    },
    showStoreFees && {
      title: <span className={headerCls}>Store Fees</span>,
      key: 'extrafees',
      render: (_, order, idx) => (
        <span className={numCell} data-tooltip-id={`extrafee-${idx}`}>
          {order.extrafees && order.extrafees.length > 0 && (
            <Tooltip id={`extrafee-${idx}`} place="top" effect="solid">
              {order.extrafees.map((extrafee, i) => (
                <div key={i}>
                  {extrafee.name} - {formatCurrency(extrafee.price)}
                </div>
              ))}
            </Tooltip>
          )}
          {order.totalextrafee ? formatCurrency(order.totalextrafee) : '$0.00'}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Tip</span>,
      key: 'tip',
      render: (_, order) => (
        <span className={numCell}>{formatCurrency(order.tip)}</span>
      ),
    },
    {
      title: <span className={headerCls}>Card Fees</span>,
      key: 'cardfees',
      render: (_, order) => (
        <span className={negCell}>
          (
          {formatCurrency(
            order.stripefee > order.cardfee ? order.stripefee : order.cardfee,
          )}
          )
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Discount</span>,
      key: 'discount',
      render: (_, order) => (
        <span className={negCell}>({formatCurrency(order.discount)})</span>
      ),
    },
    {
      title: <span className={headerCls}>Fee</span>,
      key: 'thiswayfee',
      render: (_, order) => (
        <span className={negCell}>({formatCurrency(order.thiswayfee)})</span>
      ),
    },
    {
      title: <span className={headerCls}>Refund</span>,
      key: 'refund',
      render: (_, order) => (
        <span className={negCell}>({formatCurrency(order.refund)})</span>
      ),
    },
    {
      title: <span className={headerCls}>Service Charge</span>,
      key: 'fee',
      render: (_, order) => (
        <span className={negCell}>({formatCurrency(order.fee)})</span>
      ),
    },
    {
      title: <span className={headerCls}>Net Payout</span>,
      key: 'netpayout',
      render: (_, order) => (
        <span className="text-sm font-semibold tabular-nums text-slate-900 dark:text-slate-100">
          {calculateNetPayout(order)}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Stripe Net</span>,
      key: 'stripenet',
      render: (_, order) => (
        <span className={numCell}>
          {formatCurrency(order.total - (order.stripefee + order.refund))}
        </span>
      ),
    },
    {
      title: <span className={headerCls}>Total Amount</span>,
      key: 'total',
      render: (_, order) => (
        <span className="text-sm font-bold tabular-nums text-slate-900 dark:text-slate-100">
          {formatCurrency(order.total)}
        </span>
      ),
    },
  ].filter(Boolean);

  return (
    <div className="min-h-[calc(100vh-4rem)] bg-white px-4 py-6 dark:bg-slate-900/40 sm:px-6 lg:px-8 lg:py-8">
      <div className="mx-auto max-w-5xl space-y-8">
        <PageHeader
          title="Orders Summary"
          description="You can see the summary of orders here."
          backTo="/dashboard/orders"
          backLabel="Back to orders"
        />

        {loading.listLoading ? (
          <OrdersSummarySkeleton rows={6} showStoreFees={showStoreFees} />
        ) : orderList.length > 0 ? (
          <div className="reveal">
            <TWDataTable
              columns={columns}
              data={orderList}
              rowKey="id"
              rowClickPath={(order) => `/dashboard/orders/${order.id}`}
              pagination={
                pagination.totalRecords > pagination.pageSize
                  ? {
                      current: pagination.pageNumber,
                      pageSize: pagination.pageSize,
                      total: pagination.totalRecords,
                      showTotal: (total, range) =>
                        `Showing ${range[0]} to ${range[1]} of ${total} results`,
                      onChange: (page) =>
                        setPagination((prev) => ({
                          ...prev,
                          pageNumber: page,
                        })),
                      showSizeChanger: false,
                    }
                  : undefined
              }
            />
          </div>
        ) : (
          <EmptyState image={NoTransactions} title="No Orders Summary" />
        )}
      </div>
    </div>
  );
};

export default OrdersSummary;
